import React, { useState, useEffect, useCallback, useRef } from "react";
import { createOutfit, deleteOutfit, getUserById, updateOutfit } from "../../apis";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import actions from "../../actions";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FiMoreVertical } from "react-icons/fi";
import SimpleOutfitEditor from "./OutfitEditor";
import "./outfit.css";
import { Pagination } from "@mui/material";

const OutfitPage = ({ stopLoadingRequest, startLoadingRequest, activateModal }) => {
  const [outfits, setOutfits] = useState([]);
  const [wardrobe, setWardrobe] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { id } = useParams(); // user id
  const [user, setUser] = useState([]);
  const [selectedOutfit, setSelectedOutfit] = useState("");

  const [fullScreenImage, setFullScreenImage] = useState(null);

  // Outfit fields
  const [createdOutfit, setCreatedOutfit] = useState(null); // State to store returned outfit data
  const [name, setName] = useState("");
  const [image, setImage] = useState("");
  const [description, setDescription] = useState("");
  const [occasion, setOccasion] = useState("");
  const [tags, setTags] = useState("");
  // For simplicity, isActive is not handled in the modal form as it may be managed internally

  const [dropdownOpen, setDropdownOpen] = useState(null);
  const dropdownRefs = useRef([]);

  // Pagination and Search States
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  // Reset current page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Fetch outfits data from user details API (assumes outfits are in data.outfits)
  const fetchOutfits = useCallback(async () => {
    try {
      startLoadingRequest();
      const apiResponse = await getUserById(id);
      const data = apiResponse?.data?.data;
      setUser(data);
      setOutfits(data?.outfits || []);
      console.log("data?.wardrob", data);
      setWardrobe(data?.waredrob || []);
    } catch (error) {
      setOutfits([]);
    } finally {
      stopLoadingRequest();
    }
  }, [startLoadingRequest, stopLoadingRequest, id]);

  useEffect(() => {
    fetchOutfits();
  }, [fetchOutfits]);

  const handleOutfitCreated = (data) => {
    console.log("Outfit created:", data);
    setCreatedOutfit(data);
    setImage(data?.combinedImageUrl);
    // You can also update the outfits list or perform other actions here.
  };

  const handleAddOutfit = () => {
    setSelectedOutfit("");
    // Reset all fields
    setName("");
    setImage("");
    setDescription("");
    setOccasion("");
    setTags("");
    setIsModalOpen(true);
  };

  const handleEditOutfit = (outfit) => {
    setSelectedOutfit(outfit._id);
    setName(outfit.name);
    setImage(outfit.image);
    setDescription(outfit.description);
    setOccasion(Array.isArray(outfit.occasion) ? outfit.occasion.join(", ") : "");
    setTags(Array.isArray(outfit.tags) ? outfit.tags.join(", ") : "");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const createNewOutfit = async () => {
    try {
      const formData = {
        userId: id,
        name,
        image,
        description,
        occasion: occasion.split(",").map((o) => o.trim()).filter((o) => o),
        tags: tags.split(",").map((t) => t.trim()).filter((t) => t),
      };

      const response = await createOutfit(formData);
      if (response && response.data) {
        await fetchOutfits();
        toast.success("Outfit created successfully!");
        closeModal();
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      toast.error("Error creating outfit. Please try again.");
    } finally {
      stopLoadingRequest();
    }
  };

  const updateExistingOutfit = async () => {
    try {
      const formData = {
        userId: id,
        outfitId: selectedOutfit,
        name,
        image,
        description,
        occasion: occasion.split(",").map((o) => o.trim()).filter((o) => o),
        tags: tags.split(",").map((t) => t.trim()).filter((t) => t),
      };

      const response = await updateOutfit(formData);
      if (response && response.data) {
        await fetchOutfits();
        toast.success("Outfit updated successfully!");
        closeModal();
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      toast.error("Error updating outfit. Please try again.");
    } finally {
      stopLoadingRequest();
    }
  };

  const handleDeleteOutfit = async (outfitId) => {
    try {
      if (window.confirm("Are you sure you want to delete this outfit?")) {
        const response = await deleteOutfit(outfitId, id);
        if (response && response.data) {
          await fetchOutfits();
          toast.success("Outfit deleted successfully!");
        } else {
          throw new Error("Invalid response from server");
        }
      }
    } catch (error) {
      toast.error("Error deleting outfit. Please try again.");
    } finally {
      stopLoadingRequest();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    startLoadingRequest();
    if (selectedOutfit) {
      updateExistingOutfit();
    } else {
      createNewOutfit();
    }
  };

  const toggleDropdown = (index) => {
    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefs.current &&
        !dropdownRefs.current.some((ref) => ref && ref.contains(event.target))
      ) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Pagination helper: slice items based on currentPage and rowsPerPage
  const paginateItems = (items) => {
    const indexOfLastItem = currentPage * rowsPerPage;
    const indexOfFirstItem = indexOfLastItem - rowsPerPage;
    return items.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Filter outfits based on search term (filtering by name, description, occasion, and tags)
  const filteredOutfits = outfits.filter((item) =>
    (item.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (Array.isArray(item.occasion)
      ? item.occasion.join(", ").toLowerCase()
      : (item.occasion || "").toLowerCase()
    ).includes(searchTerm.toLowerCase()) ||
    (Array.isArray(item.tags)
      ? item.tags.join(", ").toLowerCase()
      : (item.tags || "").toLowerCase()
    ).includes(searchTerm.toLowerCase())
  );

  const computedTotalPages = Math.ceil(filteredOutfits.length / rowsPerPage);
  const displayedOutfits = paginateItems(filteredOutfits);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  return (
    <div className="outfit-page">
      <div
        className="table-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        <h2>
          {searchTerm.trim() === "" ? (
            "Outfit Items"
          ) : displayedOutfits.length > 0 ? (
            <>
              Results for <span className="highlight">{searchTerm}</span>
            </>
          ) : (
            <>0 Outfits Found!</>
          )}
        </h2>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
     {user.isAccess ? <div className="table-container" style={{ marginTop: "-20px",height:'65vh' }}>
        <table className="outfit-table" id="outfit-table-color">
          <thead>
            <tr>
              <th>#</th>
              <th>Image</th>
              <th>Name</th>
              <th>Description</th>
              <th>Occasion</th>
              <th>Tags</th>
              <th>Times Worn</th>
              <th>Time Worn This Month</th>
              <th>Active</th>
              <th>Created At</th>
              <th>Updated At</th>
              {/* <th>Actions</th> */}
            </tr>
          </thead>
          <tbody>
            {displayedOutfits?.map((item, index) =>
              item ? (
                <tr key={item._id}>
                  <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                  <td>
                    <img
                      src={item.image}
                      alt={item.name}
                      className="outfit-image"
                      onClick={() => setFullScreenImage(item.image)}
                    />
                  </td>
                  <td>{item.name}</td>
                  <td>{item.description}</td>
                  <td>
                    {Array.isArray(item.occasion)
                      ? item.occasion.join(", ")
                      : item.occasion}
                  </td>
                  <td>
                    {Array.isArray(item.tags)
                      ? item.tags.join(", ")
                      : item.tags}
                  </td>
                  <td>{item?.wearCount||0}</td>
                  <td>{item?.wearCountInThisMonth||0}</td>
                  <td>{item.isActive ? "Yes" : "No"}</td>
                  <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                  <td>{new Date(item.updatedAt).toLocaleDateString()}</td>
                </tr>
              ) : null
            )}
          </tbody>
        </table>
        <div className="pagination-container">
          <Pagination
            count={computedTotalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </div>
      </div>:
      <div style={{width:'100%',height:"10rem", color:'red',fontSize:'15px',display:'flex',justifyContent:'center',alignItems:'center'}}>You do not have permission to access user outfits</div>
      }

      {fullScreenImage && (
        <div className="full-screen-overlay" onClick={() => setFullScreenImage(null)}>
          <img src={fullScreenImage} alt="Full Screen" className="full-screen-image" />
        </div>
      )}

      {/* SimpleOutfitEditor */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal_outfit-content">
            <div className="modal-header">
              <h2>{selectedOutfit ? "Edit Outfit" : "Add New Outfit"}</h2>
              <span className="close" onClick={closeModal}>
                &times;
              </span>
            </div>
            <div className="df w-full" style={{ gap: "20px" }}>
              <div>
                <SimpleOutfitEditor user={user} image={image} wardrob={wardrobe} onOutfitCreated={handleOutfitCreated} />
              </div>
              <div>
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  {/* Uncomment the following block if you wish to edit image URL manually */}
                  {/* <div className="form-group">
                    <label htmlFor="image">Image URL</label>
                    <input
                      type="text"
                      id="image"
                      name="image"
                      required
                      value={image}
                      onChange={(e) => setImage(e.target.value)}
                    />
                  </div> */}
                  <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      name="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="occasion">Occasion (comma separated)</label>
                    <input
                      type="text"
                      id="occasion"
                      name="occasion"
                      value={occasion}
                      onChange={(e) => setOccasion(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="tags">Tags (comma separated)</label>
                    <input
                      type="text"
                      id="tags"
                      name="tags"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                    />
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="cancel-button" onClick={closeModal}>
                      Cancel
                    </button>
                    <button type="submit" className="submit-button">
                      Save
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      stopLoadingRequest: actions.loader.stopLoading,
      startLoadingRequest: actions.loader.startLoading,
      activateModal: actions.modal.activateModal,
    },
    dispatch
  );

export default connect(null, mapDispatchToProps)(OutfitPage);
